import { memo, useMemo, useCallback } from 'react';
import type { CSSProperties } from 'react';
import { Loader, Select, Tooltip } from '@mantine/core';
import type {
  ComboboxItem,
  ComboboxLikeRenderOptionInput,
  OptionsFilter,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IconMapPin, IconUser, IconCalendar } from '@tabler/icons-react';
import { StatusBadge } from '@/components/common/StatusBadge';
import { TASK_STATUS_BADGE_COLORS } from '@/constants/statusColors';
import { useLocalizedDate } from '@/hooks/useLocalizedDate';
import { type TaskListItem, TaskStatus } from '@/types';
import { Z_INDEX_SELECT } from '@/constants/zIndex';
import styles from './TaskSearchSelect.module.css';

const LOCATION_ICON_SIZE = 12;
const DETAIL_ICON_SIZE = 14;
const STATUS_DOT_COLOR_SHADE = 6;

/** Server handles filtering via onSearchChange; bypass Mantine's client-side filter */
const serverSideFilter: OptionsFilter = ({ options }) => options;

interface TaskSearchSelectProps {
  value: string | null;
  onChange: (value: string | null) => void;
  tasks: TaskListItem[];
  onSearchChange?: (query: string) => void;
  isLoading?: boolean;
}

const TaskSearchSelectComponent = ({
  value,
  onChange,
  tasks,
  onSearchChange,
  isLoading,
}: TaskSearchSelectProps) => {
  const { t } = useTranslation();
  const { formatDateCompact, isOverdue } = useLocalizedDate();

  const taskMap = useMemo(
    () => new Map(tasks.map((task) => [task.id, task])),
    [tasks]
  );

  const selectData = useMemo(
    () => tasks.map((task) => ({ value: task.id, label: task.title })),
    [tasks]
  );

  const renderTaskOption = useCallback(
    ({ option }: ComboboxLikeRenderOptionInput<ComboboxItem>) => {
      const task = taskMap.get(option.value);
      if (!task) return option.label;

      const statusColor = TASK_STATUS_BADGE_COLORS[task.status] ?? 'gray';

      return (
        <div className={styles.optionRow}>
          <div className={styles.optionHeader}>
            <div className={styles.optionTitleGroup}>
              <Tooltip label={t(`taskStatus.${task.status}`)}>
                <span
                  className={styles.statusDot}
                  style={
                    {
                      '--status-dot-color': `var(--mantine-color-${statusColor}-${STATUS_DOT_COLOR_SHADE})`,
                    } as CSSProperties
                  }
                />
              </Tooltip>
              <span className={styles.optionTitle}>{task.title}</span>
            </div>
            <StatusBadge type="priority" value={task.priority} size="xs" />
          </div>

          <div className={styles.optionDetails}>
            {task.assignedToName && (
              <div className={styles.optionDetailItem}>
                <IconUser
                  size={DETAIL_ICON_SIZE}
                  color="var(--mantine-color-gray-6)"
                />
                <span>{task.assignedToName}</span>
              </div>
            )}
            {task.dueDate && (
              <div
                className={`${styles.optionDetailItem} ${styles.optionDateItem}`}
              >
                <IconCalendar
                  size={DETAIL_ICON_SIZE}
                  color="var(--mantine-color-gray-6)"
                />
                <span
                  className={
                    isOverdue(task.dueDate) &&
                    task.status !== TaskStatus.Completed
                      ? styles.optionDueDateOverdue
                      : undefined
                  }
                >
                  {formatDateCompact(task.dueDate)}
                </span>
              </div>
            )}
          </div>

          {task.location && (
            <div className={styles.optionLocation}>
              <IconMapPin
                size={LOCATION_ICON_SIZE}
                color="var(--mantine-color-gray-6)"
              />
              {task.location}
            </div>
          )}
        </div>
      );
    },
    [taskMap, t, formatDateCompact, isOverdue]
  );

  return (
    <Select
      searchable
      clearable
      label={t('notes.form.linkedTaskLabel')}
      placeholder={t('notes.form.linkedTaskPlaceholder')}
      nothingFoundMessage={t('notes.form.noTasksFound')}
      data={selectData}
      value={value}
      onChange={onChange}
      onSearchChange={onSearchChange}
      filter={serverSideFilter}
      renderOption={renderTaskOption}
      comboboxProps={{ zIndex: Z_INDEX_SELECT }}
      classNames={{ dropdown: styles.dropdown }}
      rightSection={isLoading ? <Loader size="xs" /> : undefined}
    />
  );
};

export const TaskSearchSelect = memo(TaskSearchSelectComponent);
